import cn from 'classnames'
import { FunctionComponent, h, Ref } from 'preact'
import { useContext, useMemo, useRef } from 'preact/hooks'
import BaseContext from 'contexts/base-context'
import { showFullChildElement } from 'scripts/utils'
import { Field, FieldLayout, FormFieldType, RatingField } from 'scripts/type'
import FieldHeader from './FieldHeader'
import FieldImage from '../FieldImage'
import NextPage from './NextPage'
import FieldsContext from '../../fieldsContext'
import Highlight from './Highlight'
import getFieldDomByCode from '../../utils/getFieldDomByCode'

import styles from './index.scss'
import { useHorizontalPadding } from 'components/business/SurveyForm/Fields/hooks'

type Props = {
  field: Field
  titleRef?: Ref<HTMLDivElement>
  className?: string
  handlerProps?: {
    visible?: boolean
    disabled?: boolean
    buttonRef?: Ref<HTMLDivElement>
    onNext?(): void
  }
  headerClass?: string
  titleClass?: string
  descriptionClass?: string
}

const FieldWrapper: FunctionComponent<Props> = (props) => {
  const {
    className = '',
    headerClass = '',
    titleClass = '',
    descriptionClass = '',
    field,
    children,
    titleRef,
    handlerProps = {},
  } = props
  const { disabled, onNext, buttonRef, visible } = handlerProps || {}
  const { isMultipleFields, fields } = useContext(FieldsContext)!
  const { interactive, onFieldClick, isPreview, editingCode, isUrlVisit } = useContext(BaseContext)!
  const elementRef = useRef<HTMLDivElement>(null)
  const contentHorizontalPadding = useHorizontalPadding()

  // 需求还没确定，但是感觉迟早用得上
  // const hasErr = useMemo(() => field && errFieldCodes.includes(field.code), [field, errFieldCodes])
  const hasNextPage = useMemo(() => !isMultipleFields && visible, [isMultipleFields, visible])

  const noHeader = useMemo(() => {
    if (field?.type === FormFieldType.RatingField) {
      return (field as RatingField).layout === FieldLayout.Compact
    }
    if (!field?.description && field?.labelHidden) {
      return true
    }
    return [FormFieldType.EndingField, FormFieldType.WelcomeField].includes(field?.type as any)
  }, [field])

  // 每一页第一个字段并且带有大图，称之为 Hero Field
  // Google: Hero Section
  const isFieldWithImage = !!field.image
  const isHeroField = fields.indexOf(field) === 0 && isFieldWithImage
  const isFieldPreview = isPreview && !interactive
  const isFieldSelected = editingCode === field.code

  const onClick = () => {
    if (field?.code && !interactive && isPreview) {
      onFieldClick?.(field?.code)
      const clickedField = getFieldDomByCode(field?.code)
      clickedField && showFullChildElement(clickedField)
    }
  }

  return (
    <div
      className={cn(styles.wrapper, className, {
        [styles.noHeaderWrapper]: noHeader,
        [styles.heroFieldWrapper]: isHeroField,
        [styles.previewWrapper]: isFieldPreview,
        [styles.previewHighlightWrapper]: isFieldPreview && isFieldSelected,
        [styles.previewHighlightImageFieldWrapper]: isFieldPreview && isFieldWithImage,
      })}
      data-xformcode={field?.code}
      onClick={onClick}
      ref={elementRef}
      style={{
        overflow: interactive ? 'hidden' : 'initial',
      }}
    >
      {isFieldPreview && <Highlight code={field?.code} isHero={isHeroField} />}
      <div
        className={cn(styles.container, {
          [styles.readOnly]: !interactive,
        })}
      >
        <FieldImage image={field?.image} />
        <div
          style={{
            paddingInlineStart: contentHorizontalPadding,
            paddingInlineEnd: contentHorizontalPadding,
          }}
        >
          {!noHeader && (
            <FieldHeader
              field={field}
              titleRef={titleRef}
              titleClass={titleClass}
              className={cn(children ? '' : styles.pbNone, headerClass)}
              descriptionClass={descriptionClass}
            />
          )}
          {children}
          {hasNextPage && <NextPage disabled={disabled} onNext={onNext} ref={buttonRef} />}
        </div>
      </div>
    </div>
  )
}

export default FieldWrapper
